<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { MdPreview } from 'md-editor-v3'
import 'md-editor-v3/lib/preview.css'
import { Workflow } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import MermaidLivePreview from './MermaidLivePreview.vue'

// Vista de un documento markdown con soporte de diagramas Mermaid:
// - barra superior con los diagramas detectados
// - botón "Abrir visor" sobre cada diagrama renderizado (aparece al ir bajando)
// - visor avanzado (zoom, pan, pantalla completa, exportar) en un diálogo
const props = withDefaults(
  defineProps<{
    content: string
    showDiagramBar?: boolean
  }>(),
  { showDiagramBar: true }
)

const rootRef = ref<HTMLElement | null>(null)
const showDiagramViewer = ref(false)
const diagramCode = ref('')

// El título se define con una línea `%% title: <nombre>` dentro del bloque
const DIAGRAM_TITLE_RE = /^%%\s*title:\s*(.+)$/m

const mermaidDiagrams = computed(() =>
  Array.from((props.content || '').matchAll(/```mermaid\r?\n([\s\S]*?)```/g)).map(m => {
    const code = m[1].trim()
    const titleMatch = code.match(DIAGRAM_TITLE_RE)
    return { code, title: titleMatch ? titleMatch[1].trim() : '' }
  })
)

const openDiagram = (code: string) => {
  diagramCode.value = code
  showDiagramViewer.value = true
}

const OPEN_BTN_CLASS = 'doc-mermaid-open'
const OPEN_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></svg>`

// md-editor-v3 renderiza cada diagrama como `p.md-editor-mermaid[data-content]`
// y le agrega su propia barra `.md-editor-mermaid-action` (copiar). Ahí sumamos
// nuestro botón. md-editor re-renderiza el HTML al cambiar el contenido, por eso
// se vuelve a inyectar con un MutationObserver.
const injectOpenButtons = () => {
  const root = rootRef.value
  if (!root) return
  root.querySelectorAll<HTMLElement>('.md-editor-preview .md-editor-mermaid[data-processed]').forEach((diagram) => {
    if (diagram.querySelector(`.${OPEN_BTN_CLASS}`)) return
    let actions = diagram.querySelector<HTMLElement>('.md-editor-mermaid-action')
    if (!actions) {
      actions = document.createElement('div')
      actions.className = 'md-editor-mermaid-action'
      diagram.appendChild(actions)
    }
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = OPEN_BTN_CLASS
    btn.title = 'Abrir en visor avanzado (zoom y pantalla completa)'
    btn.innerHTML = `${OPEN_ICON}<span>Abrir visor</span>`
    btn.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopPropagation()
      const code = diagram.dataset.content
      if (code) openDiagram(code)
    })
    actions.prepend(btn)
  })
}

let observer: MutationObserver | null = null

onMounted(() => {
  if (!rootRef.value) return
  observer = new MutationObserver(() => injectOpenButtons())
  observer.observe(rootRef.value, { childList: true, subtree: true })
  injectOpenButtons()
})

onBeforeUnmount(() => {
  observer?.disconnect()
  observer = null
})
</script>

<template>
  <div ref="rootRef" class="document-markdown-view">
    <div
      v-if="showDiagramBar && mermaidDiagrams.length > 0"
      class="doc-diagram-bar flex flex-wrap items-center gap-2 mb-4 p-3 bg-muted/30 rounded-lg border border-border/50"
    >
      <span class="text-xs font-medium text-muted-foreground mr-1">
        Diagramas detectados:
      </span>
      <Button
        v-for="(diagram, index) in mermaidDiagrams"
        :key="index"
        variant="outline"
        size="sm"
        class="h-7 text-xs text-primary hover:text-primary border-primary/20 hover:border-primary/50 bg-primary/5"
        :title="diagram.title || undefined"
        @click="openDiagram(diagram.code)"
      >
        <Workflow class="w-3.5 h-3.5 mr-1.5 shrink-0" />
        <span class="truncate max-w-[16rem]">
          {{ diagram.title || ('Ver Diagrama ' + (mermaidDiagrams.length > 1 ? index + 1 : '')) }}
        </span>
      </Button>
    </div>

    <div class="prose prose-sm dark:prose-invert max-w-none">
      <MdPreview :model-value="content" language="en-US" />
    </div>

    <Dialog v-model:open="showDiagramViewer">
      <DialogContent class="z-[200] max-w-6xl w-[90vw] h-[85vh] p-0 flex flex-col overflow-hidden bg-background">
        <MermaidLivePreview :code="diagramCode" />
      </DialogContent>
    </Dialog>
  </div>
</template>

<style>
/* Barra de acciones del diagrama siempre visible (también en táctil), más
   marcada al pasar el mouse */
.document-markdown-view .md-editor-preview .md-editor-mermaid[data-processed] {
  position: relative;
}
.document-markdown-view .md-editor-preview .md-editor-mermaid[data-processed] .md-editor-mermaid-action {
  opacity: 0.85;
  align-items: center;
}
.document-markdown-view .md-editor-preview .md-editor-mermaid[data-processed]:hover .md-editor-mermaid-action {
  opacity: 1;
}

.document-markdown-view .doc-mermaid-open {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  border-radius: 6px;
  border: 1px solid hsl(var(--border));
  background: hsl(var(--background) / 0.9);
  color: hsl(var(--primary));
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.08);
  cursor: pointer;
  backdrop-filter: blur(4px);
  transition: background-color 0.15s, border-color 0.15s;
}
.document-markdown-view .doc-mermaid-open:hover {
  background: hsl(var(--primary) / 0.1);
  border-color: hsl(var(--primary) / 0.5);
}
/* El svg del botón no debe heredar el fondo/padding de los iconos de md-editor */
.document-markdown-view .md-editor-preview .md-editor-mermaid[data-processed] .md-editor-mermaid-action .doc-mermaid-open svg {
  padding: 0;
  background: none;
  border-radius: 0;
}

@media print {
  .document-markdown-view .doc-diagram-bar,
  .document-markdown-view .md-editor-mermaid-action {
    display: none !important;
  }
}
</style>
